import { useEffect, useState } from 'react'
import { weatherFetch } from '../../_weatherCdn'

// Client fetch of the marine-weather forecast for one station. The product is
// static files on a CDN keyed by OUR station ids; no server involvement (the
// server must never fetch external APIs during a request).
//
// Fetch rules from the provider's guide: send NO extra headers (an
// Authorization header makes S3 reject even public objects), and don't handle
// compression ourselves (CloudFront compresses; the browser negotiates). The
// access token therefore rides in the query string, never a header — see
// _weatherCdn, which weatherFetch below routes through.
const WEATHER_BASE = 'https://d1nezfegjr0s5p.cloudfront.net/weather/forecast'

// Only the keys we dereference UNCONDITIONALLY — which is just the two arrays
// the page maps over. Everything else in the payload is read null-tolerantly
// (`data.warnings?.status`, `weather.data?.attribution?.length`), so it must
// not be validated here: this check is fail-closed, and a field listed here
// that the UI merely *prefers* turns a benign upstream change into the whole
// weather feature silently vanishing rather than one line of it degrading.
//
// Deliberately NOT checked, and why:
//   - `station.timezone` — never read at all. Day grouping and hour labels use
//     the station timezone from the Rails payload, not the feed's copy.
//   - `warnings` — WarningsHead already guards with `?.` and bails unless
//     status is 'active'. A station with no warning-feed coverage is a real
//     documented state (`note_no_warning_feed_coverage`).
//   - `attribution` — both station pages spread it with `|| []`.
//
// `conditions` IS the one exception, checked deliberately rather than left to
// each column's own null-tolerance. A station mid-registration (see
// docs/new-station-runbook.md §6) can already answer 200 with well-formed,
// non-empty `hourly`/`daily` arrays whose hours are almost entirely null —
// every reading absent except a fast-cycle model like gusts — because the
// classifier that fills `conditions` hasn't run its first cycle yet. Every
// other card keys its headline off `conditions` (the glyph, the label, the
// precip wording), so with it missing everywhere the per-field degradation
// this file is built around stops looking like "a sparse but real forecast"
// and starts looking like a table of fallback icons and a blank NOW card.
// Treating that as unusable — same as the 403 a not-yet-registered station
// gets — is what makes "isn't fully published yet" and "isn't published"
// read the same way to a visitor, which is the whole point.
export function isValidForecast(d) {
  if (!d || typeof d !== 'object') return false
  if (!Array.isArray(d.hourly) || !Array.isArray(d.daily)) return false
  return d.hourly.some((h) => h && h.conditions != null)
}

// useWeather('tide'|'current', id, enabled) →
//   { status: 'loading'|'ready'|'absent'|'error', data }
//
// - 'absent': the station isn't in the weather registry (the CDN answers 403
//   for unknown ids, with CORS intact) — weather is a bonus layer, so the UI
//   renders nothing, silently.
// - 'error': network/5xx/parse/shape failure — same quiet treatment, but
//   distinct so callers *could* differ. Note this is unrelated to
//   warnings.status === 'unavailable', which arrives inside 'ready' data.
//
// One fetch per mount: Inertia navigation remounts the page, and the browser
// cache honors the CDN's max-age=60. SSR-safe by construction — effects don't
// run during renderToString, so the server renders the 'loading' state.
export function useWeather(kind, id, enabled = true) {
  // Disabled/id-less starts as 'absent' (not 'loading') so a gated-off page
  // never server-renders a skeleton it would immediately remove.
  const [state, setState] = useState(() =>
    !enabled || id == null ? { status: 'absent', data: null } : { status: 'loading', data: null },
  )

  useEffect(() => {
    if (!enabled || id == null) {
      setState({ status: 'absent', data: null })
      return undefined
    }
    const controller = new AbortController()
    setState({ status: 'loading', data: null })
    weatherFetch(`${WEATHER_BASE}/${kind}/${id}.json`, { signal: controller.signal })
      .then(async (resp) => {
        if (resp.status === 403 || resp.status === 404) return { status: 'absent', data: null }
        if (!resp.ok) return { status: 'error', data: null }
        const data = await resp.json()
        return isValidForecast(data) ? { status: 'ready', data } : { status: 'error', data: null }
      })
      .catch(() => ({ status: 'error', data: null }))
      .then((next) => {
        if (!controller.signal.aborted) setState(next)
      })
    return () => controller.abort()
  }, [kind, id, enabled])

  return state
}
